import { forwardRef, useMemo, useRef } from 'react';
import type { PropsWithoutRef, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { mix } from 'polished';

import {
  defaultThemeProp,
  Text,
  Flex,
  Count,
  Sentiment,
  MetaList,
  DateTimeDisplay,
  Status,
  tryCatch,
  useI18n,
  useHighlight,
  createStringMatcher,
  Icon,
  registerIcon
} from '@pega/cosmos-react-core';
import type { ForwardRefForwardPropsComponent } from '@pega/cosmos-react-core';
import { StyledMetaList } from '@pega/cosmos-react-core/lib/components/MetaList/MetaList';
import * as paperClipIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/paper-clip.icon';

import type { EmailSummaryItemProps } from './Email.types';

registerIcon(paperClipIcon);

const StyledMessage = styled(Flex)<Pick<EmailSummaryItemProps, 'unreadEmailCount'>>(({
  unreadEmailCount,
  theme
}) => {
  return css`
    width: ${theme.base['content-width'].md};
    text-align: start;
    text-overflow: ellipsis;
    overflow: hidden;
    margin-inline-end: ${theme.base.spacing};
    margin-block-end: ${theme.base.spacing};
    ${unreadEmailCount !== undefined &&
    unreadEmailCount > 0 &&
    css`
      color: ${theme.base.palette.interactive};
    `}
  `;
});

export const StyledMessageAndDate = styled.div`
  white-space: nowrap;
`;

const StyledParticipants = styled(Flex)<Pick<EmailSummaryItemProps, 'unreadEmailCount'>>(({
  unreadEmailCount,
  theme
}) => {
  return css`
    ${unreadEmailCount !== undefined &&
    unreadEmailCount > 0 &&
    css`
      position: relative;
      &::before {
        content: '';
        position: absolute;
        inset-inline: calc(-1.5 * ${theme.base.spacing});
        inset-block-start: calc(0.8 * ${theme.base.spacing});
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        background-color: ${theme.base.palette.interactive};
      }
    `}
  `;
});

const StyledStatuses = styled.div``;

export const StyledEmailSummaryItem = styled.li(({ theme }) => {
  const hoverColor = tryCatch(() =>
    mix(0.85, theme.base.palette['primary-background'], theme.base.palette.interactive)
  );
  return css`
    width: 100%;
    color: ${theme.base.palette['foreground-color']};
    background-color: ${theme.base.palette['primary-background']};
    padding: ${theme.base.spacing} calc(2 * ${theme.base.spacing});
    outline: none;
    cursor: pointer;
    ${StyledMetaList}, ${StyledMessageAndDate}, ${StyledStatuses} {
      margin-block-start: ${theme.base.spacing};
    }
    & + & {
      margin-inline-start: 0;
    }

    &[aria-selected='true'],
    &[data-current='true'] {
      background-color: ${theme.base.palette['secondary-background']};
    }

    &:hover,
    &:focus-within {
      background-color: ${hoverColor};
    }
    &:not(:last-child) {
      border-block-end: 0.0625rem solid ${theme.base.palette['border-line']};
    }
  `;
});

StyledEmailSummaryItem.defaultProps = defaultThemeProp;
StyledParticipants.defaultProps = defaultThemeProp;
StyledMessage.defaultProps = defaultThemeProp;

const EmailSummaryItem: ForwardRefForwardPropsComponent<EmailSummaryItemProps> = forwardRef(
  function EmailSummaryItem(
    {
      id,
      activeParticipants,
      sentiment,
      message,
      timeStamp,
      topic,
      urgency,
      active = false,
      onSelect,
      unreadEmailCount,
      drafts,
      undelivered,
      searchQuery,
      attachmentCount,
      ...restProps
    }: PropsWithoutRef<EmailSummaryItemProps>,
    ref: EmailSummaryItemProps['ref']
  ) {
    const t = useI18n();
    const participantsRef = useRef<HTMLSpanElement>(null);
    const highlightPattern = useMemo(
      () => (searchQuery ? createStringMatcher(searchQuery, 'contains', 'ig') : undefined),
      [searchQuery]
    );
    const items = useMemo(() => {
      const itemList: ReactNode[] = [];

      if (typeof topic === 'string' && topic.length > 0) {
        itemList.push(
          <Status variant='info' key='info'>
            {topic}
          </Status>
        );
      }

      if (sentiment) {
        itemList.push(<Sentiment variant={sentiment.variant} labelHidden />);
      }

      if (typeof urgency === 'number') {
        itemList.push(<Text variant='secondary'>{`${t('priority')} ${urgency}`}</Text>);
      }
      if (typeof attachmentCount === 'number' && attachmentCount > 0) {
        itemList.push(
          <Flex container={{ gap: 0.5 }}>
            <Icon name='paper-clip' />
            <Count>{attachmentCount}</Count>
          </Flex>
        );
      }

      return itemList;
    }, [sentiment, topic, urgency, attachmentCount]);

    const emailParticipants = useMemo(() => {
      let participants = '';
      if (activeParticipants.length > 0) {
        participants =
          activeParticipants.length === 1
            ? activeParticipants[0].fullName
            : activeParticipants.map(participant => `${participant.shortName}`).join(', ');
      }
      return participants;
    }, [activeParticipants]);
    useHighlight(participantsRef, highlightPattern);
    const itemStatus = `${unreadEmailCount ? `${t('unread')}, ` : ''}${
      undelivered ? `${t('undelivered')}, ` : ''
    }${drafts ? `${t('drafts')}, ` : ''}`;

    return (
      <StyledEmailSummaryItem
        ref={ref}
        {...restProps}
        id={id}
        onClick={() => onSelect(id)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            onSelect(id);
          }
        }}
        aria-selected={active}
        aria-label={t('email_attachment', [
          `${itemStatus}${emailParticipants}, ${message} ${timeStamp} ${topic} ${
            sentiment?.variant
          } ${t('priority')} ${urgency}`,
          `${typeof attachmentCount === 'number' && attachmentCount > 0 ? t('attachments_count', [attachmentCount]) : ''}`
        ])}
        tabIndex={active ? 0 : -1}
      >
        <StyledParticipants
          unreadEmailCount={unreadEmailCount}
          container={{ justify: 'between', alignItems: 'center' }}
        >
          <Text variant='h4'>
            <span ref={participantsRef}>{emailParticipants}</span>
          </Text>
          {typeof unreadEmailCount === 'number' && unreadEmailCount > 0 && (
            <Count>{unreadEmailCount}</Count>
          )}
        </StyledParticipants>
        <Flex
          container={{
            justify: 'between'
          }}
          as={StyledMessageAndDate}
        >
          <StyledMessage unreadEmailCount={unreadEmailCount}>{message}</StyledMessage>
          <DateTimeDisplay value={timeStamp} variant='date' />
        </Flex>
        <MetaList items={items} />
        {(undelivered || drafts) && (
          <Flex
            container={{
              justify: 'start',
              gap: 1
            }}
            as={StyledStatuses}
          >
            {undelivered && <Status variant='urgent'>{t('undelivered')}</Status>}
            {/* Passing count as infinity so that translation engine select correct form of draft,
                 with [](second argument) as empty so that count is not shown */}
            {drafts && <Status variant='pending'>{t('draft', [], { count: Infinity })}</Status>}
          </Flex>
        )}
      </StyledEmailSummaryItem>
    );
  }
);

export default EmailSummaryItem;
